// SalesOrderView.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Trash2, Printer, Settings, RefreshCw, ChevronLeft, ChevronRight,
  MessageCircle, Wallet, FileText, Eye, GripVertical, Copy, Pencil,
} from "lucide-react";
import { fmt } from "../../../../../services/erpApi";
import StageBar, { SALE_ORDER_STAGES } from "../../../../../components/admin/StageBar";
import CustomerSelect from "../../../../../components/admin/CustomerSelect";

/* ------------------------------------------------------------------ */
/* STYLES                                                             */
/* ------------------------------------------------------------------ */
const s = {
  page: { minHeight: "100vh", background: "#f6f7f8", color: "#263746", fontFamily: '"Lucida Grande", Helvetica, Verdana, Arial, sans-serif' },
  topbar: { minHeight: 38, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", background: "#fff", borderBottom: "1px solid #d8dde2", fontSize: 12 },
  breadcrumb: { display: "flex", alignItems: "center", gap: 8, color: "#63758a" },
  crumbLink: { color: "#714b67", cursor: "pointer" },
  crumbSep: { color: "#b8c0c8" },
  topActions: { display: "flex", alignItems: "center", gap: 18, color: "#526170" },
  topAction: { display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: 12 },
  pager: { display: "flex", alignItems: "center", gap: 10, color: "#526170" },
  pagerArrow: { cursor: "pointer", color: "#97a0a8" },
  newBtn: { border: "1px solid #714b67", background: "#fff", color: "#714b67", padding: "4px 12px", fontSize: 12, cursor: "pointer" },

  actionBar: { display: "flex", alignItems: "center", background: "#fff", borderBottom: "1px solid #d8dde2", minHeight: 36 },
  actionBtn: { padding: "8px 14px", border: 0, borderRight: "1px solid #e0e4e7", background: "#fff", color: "#526170", cursor: "pointer", fontSize: 12, textTransform: "uppercase", letterSpacing: ".2px" },
  actionBtnPrimary: { padding: "8px 14px", border: 0, borderRight: "1px solid #e0e4e7", background: "#7c7bad", color: "#fff", cursor: "pointer", fontSize: 12, textTransform: "uppercase", letterSpacing: ".2px" },
  stages: { marginLeft: "auto", display: "flex", alignItems: "center", fontSize: 11 },
  stage: { padding: "9px 18px", color: "#97a0a8", background: "#fff", borderLeft: "1px solid #e0e4e7", cursor: "default" },
  stageActive: { padding: "9px 18px", color: "#fff", background: "#263d4a", position: "relative" },

  container: { width: "min(100%, 1240px)", margin: "0 auto", background: "#fff", border: "1px solid #e0e4e7", borderTop: 0 },

  /* Smart buttons */
  smartBar: { display: "flex", justifyContent: "flex-end", borderBottom: "1px solid #e0e4e7" },
  smartBtn: { display: "flex", alignItems: "center", gap: 10, padding: "10px 22px", border: 0, borderLeft: "1px solid #e0e4e7", background: "#fff", cursor: "pointer", minWidth: 150 },
  smartBtnText: { display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.15, fontSize: 12, color: "#263746" },
  smartCount: { fontSize: 15, fontWeight: 600 },

  /* Header */
  sheet: { padding: "24px 28px 30px" },
  docTitle: { fontSize: 26, fontWeight: 600, margin: "0 0 22px", color: "#263746" },
  headerGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 60, rowGap: 4 },
  fieldRow: { display: "grid", gridTemplateColumns: "145px 1fr", alignItems: "center", minHeight: 30, fontSize: 12, color: "#526170" },
  fieldLabel: { color: "#526170" },
  readonly: { borderBottom: "1px solid #eef1f3", padding: "5px 2px", color: "#34495a", minHeight: 14 },
  input: { width: "100%", boxSizing: "border-box", border: 0, borderBottom: "1px solid #aeb8c1", padding: "5px 2px", outline: 0, color: "#34495a", background: "transparent", fontSize: 12 },
  readonlyText: { padding: "5px 2px", color: "#34495a" },

  /* Tabs */
  tabs: { display: "flex", gap: 0, borderBottom: "1px solid #dce1e5", marginTop: 26, padding: "0 0 0 0" },
  tab: { padding: "10px 16px", cursor: "pointer", fontSize: 12, color: "#63758a", border: "1px solid transparent", borderBottom: 0, background: "transparent", marginBottom: -1 },
  tabActive: { padding: "10px 16px", cursor: "pointer", fontSize: 12, color: "#263746", background: "#fff", border: "1px solid #dce1e5", borderBottom: "1px solid #fff", marginBottom: -1 },

  /* Order lines table */
  tableWrap: { border: "1px solid #e0e4e7", marginTop: 12, overflowX: "auto" },
  tableHead: { display: "grid", gridTemplateColumns: "24px 2fr 2fr 1fr .8fr .8fr .7fr 1.1fr 1.4fr 1fr 24px", gap: 0, background: "#f3f4f6", color: "#405161", fontSize: 11, fontWeight: 600, borderBottom: "1px solid #e0e4e7", minWidth: 1080 },
  th: { padding: "8px 10px", borderRight: "1px solid #e0e4e7" },
  tableRow: { display: "grid", gridTemplateColumns: "24px 2fr 2fr 1fr .8fr .8fr .7fr 1.1fr 1.4fr 1fr 24px", gap: 0, borderBottom: "1px solid #eef1f3", minWidth: 1080, fontSize: 12, color: "#405161", alignItems: "center" },
  td: { padding: "8px 10px", borderRight: "1px solid #f3f4f6" },
  tdRight: { padding: "8px 10px", textAlign: "right", borderRight: "1px solid #f3f4f6" },
  cellInput: { width: "100%", boxSizing: "border-box", border: 0, background: "transparent", padding: "4px 2px", outline: 0, fontSize: 12, color: "#405161" },
  grip: { color: "#b8c0c8", cursor: "grab", display: "flex", justifyContent: "center" },
  gripCell: { display: "flex", justifyContent: "center", padding: "8px 0", borderRight: "1px solid #f3f4f6" },
  taxBadge: { display: "inline-block", background: "#eef1f3", border: "1px solid #dce1e5", borderRadius: 2, padding: "2px 6px", fontSize: 11, color: "#526170" },
  addLineRow: { display: "flex", gap: 18, padding: "10px 12px", borderTop: "1px solid #eef1f3", fontSize: 12, color: "#714b67" },
  addLink: { color: "#714b67", cursor: "pointer", border: 0, background: "transparent", fontSize: 12 },

  /* Totals block */
  totalsBlock: { display: "flex", justifyContent: "flex-end", padding: "16px 28px 8px" },
  totalsTable: { width: 320, fontSize: 12, color: "#526170" },
  totalRow: { display: "flex", justifyContent: "space-between", padding: "4px 0" },
  totalFinal: { display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid #dce1e5", fontWeight: 700, color: "#263746" },
  terms: { padding: "0 28px 30px", fontSize: 12, color: "#526170" },

  /* Other info tab */
  infoGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 60, padding: "20px 28px" },
  sectionTitle: { fontSize: 11, fontWeight: 700, color: "#526170", textTransform: "uppercase", letterSpacing: ".5px", margin: "18px 0 10px", borderBottom: "1px solid #dce1e5", paddingBottom: 6 },
  checkboxLine: { display: "flex", alignItems: "center", gap: 12, fontSize: 12, color: "#526170", padding: "6px 0" },

  /* Note for contract tab */
  noteField: { display: "grid", gridTemplateColumns: "145px 1fr", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid #eef1f3", fontSize: 12, color: "#526170" },

  /* Subscription option tab */
  subGrid: { padding: "20px 28px" },
  subField: { display: "grid", gridTemplateColumns: "180px 1fr", alignItems: "center", padding: "8px 0", fontSize: 12, color: "#526170" },
  subInput: { borderBottom: "1px solid #e0e4e7", minHeight: 22, padding: "4px 0", color: "#34495a" },

  /* Subscription invoice tab */
  subInvTable: { margin: "16px 28px", border: "1px solid #e0e4e7" },
  subInvHead: { display: "grid", gridTemplateColumns: "40px 1.2fr 1.2fr 1.4fr", background: "#f3f4f6", fontSize: 11, fontWeight: 600, color: "#405161", borderBottom: "1px solid #e0e4e7" },
  subInvRow: { display: "grid", gridTemplateColumns: "40px 1.2fr 1.2fr 1.4fr", fontSize: 12, color: "#405161", minHeight: 34, borderBottom: "1px solid #eef1f3" },
  subInvCell: { padding: "8px 10px", borderRight: "1px solid #f3f4f6" },
};

/* ------------------------------------------------------------------ */
/* FIELD réutilisable                                                 */
/* ------------------------------------------------------------------ */
function Field({ label, editable, value, onChange, type = "text", options, placeholder, children, withIcon }) {
  return (
    <label style={s.fieldRow}>
      <span style={s.fieldLabel}>{label}{withIcon}</span>
      {editable ? (
        type === "select" ? (
          <select value={value ?? ""} onChange={(e) => onChange?.(e.target.value)} style={s.input}>
            <option value="">{placeholder || ""}</option>
            {options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : type === "checkbox" ? (
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={!!value} onChange={(e) => onChange?.(e.target.checked)} />
            {placeholder}
          </span>
        ) : (
          <input type={type} value={value ?? ""} onChange={(e) => onChange?.(e.target.value)} placeholder={placeholder} style={s.input} />
        )
      ) : (
        <span style={s.readonlyText}>{children ?? (value == null || value === "" ? "" : value)}</span>
      )}
    </label>
  );
}

/* ------------------------------------------------------------------ */
/* SMART BUTTON                                                       */
/* ------------------------------------------------------------------ */
function SmartButton({ icon: Icon, count, label, onClick, color = "#714b67", showBadge }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...s.smartBtn, background: hover ? "#f8f9fa" : "#fff" }}
    >
      <div style={{ position: "relative" }}>
        <Icon size={22} color={color} strokeWidth={1.6} />
      </div>
      <div style={s.smartBtnText}>
        {count != null && (
          <span style={{ ...s.smartCount, color }}>
            {count}
            {showBadge && <span style={{
              display: "inline-block", width: 6, height: 6, borderRadius: "50%",
              background: "#e74c3c", marginLeft: 4, verticalAlign: "middle"
            }} />}
          </span>
        )}
        <span>{label}</span>
      </div>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* MAIN                                                               */
/* ------------------------------------------------------------------ */
export default function SalesOrderView({
  order,
  editable = false,
  form,
  setForm,
  partners = [],
  products = [],
  onBack,
  onConfirm,
  onCancel,
  onInvoiceClick,
  onSendEmail,
  onRefresh,
  onNew,
  onSave,
  onEdit,
  onCancelEdit,
  onAddLine,
  onUpdateLine,
  onRemoveLine,
  onChooseProduct,
  onStageChange,
  onSubscriptionRenew,
  onExpensesClick,
  onCustomerPreview,
  onPrint,
  onDeliver,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("lines");
  const [renewBusy, setRenewBusy] = useState(false);

  const isSalesOrder = order.status === "sales_order";
  const isQuotationSent = order.status === "quotation_sent";
  const isQuotation = order.status === "quotation";

  // valeur : form si éditable, sinon order
  const v = (key, fallback) => (editable && form ? form[key] : order[key] ?? fallback);
  const set = (key) => (val) => setForm?.((c) => ({ ...c, [key]: val }));

  const lines = editable && form ? form.lines : order.lines;

  const subtotal = lines?.reduce((acc, l) => acc + (Number(l.qty) || 0) * (Number(l.price ?? l.price_unit) || 0), 0) || 0;
  const tax = Math.round(subtotal * 0.1925);
  const total = subtotal + tax;

  const currentStageId = isSalesOrder ? "sale" : isQuotationSent ? "sent" : "draft";

  const handleStageSelect = (stage) => {
    if (!onStageChange) return;
    if (stage.id === currentStageId) return;
    if (stage.id === "sale") onStageChange("confirm");
    else if (stage.id === "sent") onStageChange("sent");
    else if (stage.id === "draft") onStageChange("cancel");
  };

  const handleSubscriptionRenew = async () => {
    if (!onSubscriptionRenew) { setRenewBusy(true); setTimeout(() => setRenewBusy(false), 800); return; }
    setRenewBusy(true);
    try { await onSubscriptionRenew(); } finally { setRenewBusy(false); }
  };

  return (
    <div style={s.page}>
      {/* -------- TOP BAR -------- */}
      <div style={s.topbar}>
        <div style={s.breadcrumb}>
          <span style={s.crumbLink} onClick={onBack}>Invoices</span>
          <span style={s.crumbSep}>/</span>
          <span style={s.crumbLink} onClick={onBack}>INV/2026/00007</span>
          <span style={s.crumbSep}>/</span>
          <strong>{order.reference}</strong>
        </div>
        <div style={s.topActions}>
          <span style={s.topAction} onClick={onPrint ? onPrint : () => window.print()} title={onPrint ? "Télécharger PDF" : "Print"}><Printer size={14} /> Print</span>
          <span style={s.topAction} onClick={() => { if (onConfirm && isQuotation) onConfirm(); }} title="Duplicate / Cancel via menu" role="button" tabIndex={0}><Settings size={14} /> Action</span>
          <span style={s.topAction} onClick={onRefresh}><RefreshCw size={14} /></span>
          <span style={s.pager}>
            <span>1 / 1</span>
            <ChevronLeft size={14} style={{ ...s.pagerArrow, opacity: hasPrev === false ? 0.3 : 1, pointerEvents: hasPrev === false ? "none" : "auto" }} onClick={onPrev} />
            <ChevronRight size={14} style={{ ...s.pagerArrow, opacity: hasNext === false ? 0.3 : 1, pointerEvents: hasNext === false ? "none" : "auto" }} onClick={onNext} />
          </span>
          {onNew && <button type="button" onClick={onNew} style={s.newBtn}>New</button>}
        </div>
      </div>

      {/* -------- ACTION BAR -------- */}
      <div style={s.actionBar}>
        {editable ? (
          <>
            <button type="button" style={s.actionBtnPrimary} onClick={onSave}>SAVE</button>
            <button type="button" style={s.actionBtn} onClick={onCancelEdit}>DISCARD</button>
            {(isQuotation || isQuotationSent) && onConfirm && (
              <button type="button" style={{ ...s.actionBtnPrimary, opacity: 0.85 }} onClick={async () => { await onSave?.(); onConfirm?.(); }} title="Sauvegarde puis confirme">CONFIRM</button>
            )}
          </>
        ) : (
          <>
            {onEdit && <button type="button" style={s.actionBtn} onClick={onEdit}>EDIT</button>}
            {onSendEmail && <button type="button" style={s.actionBtn} onClick={onSendEmail}>SEND BY EMAIL</button>}
            {onCancel && <button type="button" style={s.actionBtn} onClick={onCancel}>CANCEL</button>}
            {isSalesOrder && onDeliver && (
              <button type="button" style={s.actionBtn} onClick={onDeliver}>DELIVER</button>
            )}
            {isSalesOrder && onInvoiceClick && (
              <button type="button" style={s.actionBtnPrimary} onClick={onInvoiceClick}>CREATE INVOICE</button>
            )}
            {order.isSubscription && (
              <button type="button" style={s.actionBtn} onClick={handleSubscriptionRenew} disabled={renewBusy}>
                {renewBusy ? "RENEWING…" : "SUBSCRIPTION RENEW"}
              </button>
            )}
            {(isQuotation || isQuotationSent) && onConfirm && (
              <button type="button" style={s.actionBtnPrimary} onClick={onConfirm}>CONFIRM</button>
            )}
          </>
        )}

        {/* StageBar cliquable */}
        <div style={{ marginLeft: "auto", flex: "1 1 auto", maxWidth: 420, display: "flex" }}>
          <StageBar stages={SALE_ORDER_STAGES} currentStageId={currentStageId} onSelect={handleStageSelect} />
        </div>
      </div>

      {/* -------- CONTAINER -------- */}
      <div style={s.container}>

        {/* -------- SMART BUTTONS -------- */}
        <div style={s.smartBar}>
          {onSendEmail && (
            <SmartButton icon={MessageCircle} label="Send" onClick={onSendEmail} color="#00a09d" />
          )}
          <SmartButton icon={Wallet} count={order.expensesCount ?? 0} label="Expenses" onClick={onExpensesClick || (() => {})} color="#714b67" showBadge={order.expensesCount > 0} />
          <SmartButton icon={Pencil} count={order.invoicesCount ?? 0} label="Invoices" onClick={onInvoiceClick} color="#714b67" showBadge={order.invoicesCount > 0} />
          <SmartButton icon={Eye} label="Customer Preview" onClick={onCustomerPreview || (() => window.open(`/api/sales-orders/${order.raw?.id}/portal`, "_blank"))} color="#714b67" />
        </div>

        {/* -------- HEADER -------- */}
        <div style={s.sheet}>
          <h1 style={s.docTitle}>{order.reference}</h1>

          <div style={s.headerGrid}>
            {/* Essentiel uniquement — le reste était local-only non persisté */}
            <div>
              {editable ? (
                <label style={s.fieldRow}>
                  <span style={s.fieldLabel}>Customer *</span>
                  <CustomerSelect value={form?.partner_id ?? order.raw?.partner_id} onChange={(id) => setForm?.((c) => ({ ...c, partner_id: id }))} placeholder="Search customer… (tape 2 lettres)" />
                </label>
              ) : (
                <Field label="Customer" editable={false} value={v("partner_id", order.raw?.partner_id)}>
                  {order.raw?.partner_id ? (
                    <span style={{ color: "#714b67", cursor: "pointer", textDecoration: "underline" }} onClick={() => navigate(`/admin-panel/crm/customers`)} title={`Partner #${order.raw.partner_id}`}>{order.customer}</span>
                  ) : order.customer}
                </Field>
              )}
              <Field label="Salesperson" editable={editable} value={v("salesperson", order.raw?.user_name)} onChange={set("salesperson")}>
                {order.raw?.user_name || order.customer}
              </Field>
            </div>
            <div>
              <Field label="Order Date" editable={editable} type={editable ? "date" : "text"} value={editable ? form?.orderDate : order.raw?.date_order?.slice(0, 10)} onChange={set("orderDate")}>
                {order.orderDate}
              </Field>
              <Field label="Expiration" editable={editable} type="date" value={v("valid_until", order.raw?.valid_until?.slice(0,10))} onChange={set("valid_until")}>
                {order.raw?.valid_until ? order.raw.valid_until.slice(0,10) : "—"}
              </Field>
            </div>
          </div>

          {/* -------- TABS — essentiel uniquement -------- */}
          <div style={s.tabs}>
            <button type="button" style={s.tabActive}>Order Lines *</button>
          </div>

          {/* =============== TAB: ORDER LINES — simplifié =============== */}
          <>
              <div style={{ ...s.tableWrap, border: "1px solid #e0e4e7" }}>
                <div style={{ ...s.tableHead, gridTemplateColumns: "1.4fr 1.6fr .7fr .9fr .9fr 28px", minWidth: 0 }}>
                  <div style={s.th}>Product *</div>
                  <div style={s.th}>Description</div>
                  <div style={s.th}>Quantity</div>
                  <div style={s.th}>Unit Price</div>
                  <div style={s.th}>Subtotal</div>
                  <div style={s.th}></div>
                </div>

                {lines.map((l, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "1.4fr 1.6fr .7fr .9fr .9fr 28px", gap: 0, borderBottom: "1px solid #eef1f3", minWidth: 0, fontSize: 12, color: "#405161", alignItems: "center" }}>
                    {editable ? (
                      <>
                        <div style={s.td}><select value={l.product_id || ""} onChange={(e) => onChooseProduct?.(i, e.target.value)} style={s.cellInput}><option value="">—</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                        <div style={s.td}><input value={l.description || l.product || ""} onChange={(e) => onUpdateLine?.(i, { description: e.target.value })} placeholder="Description" style={s.cellInput} /></div>
                        <div style={s.td}><input type="number" value={l.qty || 0} onChange={(e) => onUpdateLine?.(i, { qty: e.target.value })} style={s.cellInput} /></div>
                        <div style={s.td}><input type="number" value={l.price || 0} onChange={(e) => onUpdateLine?.(i, { price: e.target.value })} style={s.cellInput} /></div>
                        <div style={s.tdRight}><strong>{fmt.money((Number(l.qty) || 0) * (Number(l.price) || 0))}</strong></div>
                        <div style={{ ...s.gripCell, borderRight: 0 }}><button type="button" onClick={() => onRemoveLine?.(i)} style={{ border: 0, background: "transparent", color: "#be123c", cursor: "pointer" }}><Trash2 size={13} /></button></div>
                      </>
                    ) : (
                      <>
                        <div style={s.td}>{l.product}</div>
                        <div style={s.td}>{l.description}</div>
                        <div style={s.td}>{Number(l.qty).toFixed(2)}</div>
                        <div style={s.tdRight}>{fmt.money(l.price)}</div>
                        <div style={s.tdRight}><strong>{fmt.money(l.subtotal)}</strong></div>
                        <div style={s.gripCell} />
                      </>
                    )}
                  </div>
                ))}
                {editable ? (<div style={s.addLineRow}><button type="button" onClick={onAddLine} style={s.addLink}><Plus size={12} /> Add a product</button></div>) : null}
              </div>
              <div style={{ padding: "14px 28px", fontSize: 12, color: "#526170" }}>Terms: <a href={order.termsUrl} style={{ color: "#16869b" }}>{order.termsUrl}</a></div>
              <div style={s.totalsBlock}><div style={s.totalsTable}><div style={s.totalRow}><span>Untaxed:</span><span>{fmt.money(subtotal)}</span></div><div style={s.totalRow}><span>Tax 19.25%:</span><span>{fmt.money(tax)}</span></div><div style={s.totalFinal}><span>Total:</span><span>{fmt.money(total)}</span></div></div></div>
            </>
        </div>
      </div>
    </div>
  );
}
